// WidgetIframeAutoresize
//
// Listens for `seiva:widget:resize` postMessage events from a widget app
// iframe and adjusts the iframe height to match the inner content.
//
// The widget SDK (server/lib/seiva/apps/widget_scaffold.ex → useAutoResize)
// installs a ResizeObserver on document.body and posts:
//
//   { type: "seiva:widget:resize", height: <px> }
//
// We clamp the height between MIN and MAX (defending against pathological
// content that would otherwise blow up the cockpit grid) and only react to
// messages whose `source` is this iframe's contentWindow — so two widget
// cards on the same panel can't cross-talk.
//
// Mount target: the <iframe> element. The `data-widget-id` attribute is
// used purely for debug logs — the message routing relies on `event.source`.

const MIN_HEIGHT = 120;
const MAX_HEIGHT = 1200;

export const WidgetIframeAutoresize = {
  mounted() {
    this.lastHeight = null;

    this.handler = (event) => {
      if (!event || event.source !== this.el.contentWindow) return;
      const data = event.data;
      if (!data || data.type !== "seiva:widget:resize") return;
      const raw = Number(data.height);
      if (!Number.isFinite(raw) || raw <= 0) return;

      const clamped = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, Math.ceil(raw)));
      this.lastHeight = clamped;
      this.el.style.height = `${clamped}px`;
    };

    window.addEventListener("message", this.handler);
  },

  updated() {
    // LV may have re-rendered the iframe (e.g. cache-busted src after a rebuild),
    // resetting the inline style. Re-apply the height so the widget doesn't
    // collapse to min-height between rebuild and the next ResizeObserver tick.
    if (this.lastHeight != null) {
      this.el.style.height = `${this.lastHeight}px`;
    }
  },

  destroyed() {
    if (this.handler) {
      window.removeEventListener("message", this.handler);
      this.handler = null;
    }
  },
};
